/*
  Torn Ink brand layer.
  - Scroll engine: one passive listener feeds one rAF loop. Each frame reads layout first,
    then writes, so it never forces a synchronous reflow. The backdrop eases toward the
    scroll position, which is what makes it feel like it follows the visitor.
  - Scroll reveals, manifesto word lighting, collection-index cursor preview, image fade-in.
  Vanilla, dependency-free, respects prefers-reduced-motion.
*/
(() => {
  const root = document.documentElement;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const lerp = (from, to, amount) => from + (to - from) * amount;
  const clamp01 = (value) => Math.min(1, Math.max(0, value));

  function initScrollEngine() {
    const backdrop = document.querySelector('.ti-backdrop');
    const heroes = Array.from(document.querySelectorAll('[data-ti-hero]'));
    const manifestos = Array.from(document.querySelectorAll('[data-ti-manifesto]'));
    // Horizon's squeeze layout (assets/scroll-container.js) makes .page-wrapper the scroll
    // container at ≥990px; below that the document scrolls.
    const wrapper = document.querySelector('.page-wrapper');
    const desktop = window.matchMedia('(min-width: 990px)');
    let eased = 0;
    let running = false;

    const frame = () => {
      // Read
      const scroller = desktop.matches && wrapper ? wrapper : null;
      const scrollY = scroller ? scroller.scrollTop : window.scrollY;
      const viewport = window.innerHeight;
      const maxScroll = scroller ? scroller.scrollHeight - scroller.clientHeight : root.scrollHeight - viewport;
      const target = clamp01(scrollY / Math.max(1, maxScroll));
      const heroRects = heroes.map((el) => el.getBoundingClientRect());
      const manifestoRects = manifestos.map((el) => el.getBoundingClientRect());

      // Write
      root.classList.toggle('ti-scrolled', scrollY > 24);

      if (!reduceMotion) {
        heroes.forEach((el, index) => {
          const rect = heroRects[index];
          if (rect.bottom > 0 && rect.top < viewport) {
            el.style.setProperty('--hero-p', clamp01(-rect.top / rect.height).toFixed(4));
          }
        });
      }

      manifestos.forEach((el, index) => {
        const rect = manifestoRects[index];
        if (rect.top < viewport && rect.bottom > 0) {
          const progress = (viewport * 0.9 - rect.top) / (rect.height + viewport * 0.4);
          el.style.setProperty('--mp', clamp01(progress).toFixed(4));
        }
      });

      if (reduceMotion || !backdrop) {
        running = false;
        return;
      }

      eased = lerp(eased, target, 0.08);
      backdrop.style.setProperty('--p', eased.toFixed(4));

      if (Math.abs(target - eased) > 0.0005) {
        requestAnimationFrame(frame);
      } else {
        running = false;
      }
    };

    const kick = () => {
      if (running) return;
      running = true;
      requestAnimationFrame(frame);
    };

    window.addEventListener('scroll', kick, { passive: true });
    window.addEventListener('resize', kick, { passive: true });
    if (wrapper) wrapper.addEventListener('scroll', kick, { passive: true });
    if (desktop.addEventListener) desktop.addEventListener('change', kick);
    kick();
  }

  function initReveals() {
    const elements = Array.from(document.querySelectorAll('[data-ti-reveal]'));
    if (!elements.length || reduceMotion || !('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-in');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.12 }
    );

    // Anything already on screen is shown immediately; only content below the fold animates.
    const viewport = window.innerHeight;
    elements.forEach((el) => {
      if (el.getBoundingClientRect().top < viewport * 0.9) el.classList.add('is-in');
      else observer.observe(el);
    });
    root.classList.add('ti-reveal-ready');

    // Safety net: never leave content hidden if an observer callback is missed.
    window.setTimeout(() => {
      elements.forEach((el) => el.classList.add('is-in'));
      observer.disconnect();
    }, 6000);
  }

  function initCollectionIndex() {
    if (!finePointer) return;

    document.querySelectorAll('[data-ti-cindex]').forEach((list) => {
      const preview = list.querySelector('.ti-cindex__preview');
      const rows = Array.from(list.querySelectorAll('.ti-cindex__row'));
      const images = preview ? Array.from(preview.querySelectorAll('.ti-cindex__pimg')) : [];
      if (!preview || !rows.length) return;

      let targetX = 0;
      let targetY = 0;
      let currentX = 0;
      let currentY = 0;
      let rafId = 0;
      let hovering = false;

      const loop = () => {
        currentX = lerp(currentX, targetX, 0.16);
        currentY = lerp(currentY, targetY, 0.16);
        preview.style.transform = `translate3d(${currentX.toFixed(1)}px, ${currentY.toFixed(1)}px, 0)`;
        const settled = Math.abs(currentX - targetX) + Math.abs(currentY - targetY) < 0.5;
        rafId = hovering || !settled ? requestAnimationFrame(loop) : 0;
      };

      list.addEventListener('pointermove', (event) => {
        const rect = list.getBoundingClientRect();
        targetX = event.clientX - rect.left;
        targetY = event.clientY - rect.top;
        if (!hovering) {
          currentX = targetX;
          currentY = targetY;
        }
        if (!rafId) rafId = requestAnimationFrame(loop);
      });

      rows.forEach((row, index) => {
        row.addEventListener('pointerenter', () => {
          hovering = true;
          list.classList.add('is-hovering');
          images.forEach((image, imageIndex) => image.classList.toggle('is-active', imageIndex === index));
        });
      });

      list.addEventListener('pointerleave', () => {
        hovering = false;
        list.classList.remove('is-hovering');
      });
    });
  }

  function initImageFade() {
    const selector = 'img.product-media__image, .ti-card__img, .ti-journal__media img, .ti-chapter__frame img, .ti-feature__img';

    const track = (img) => {
      if (img.dataset.tiImg) return;
      img.dataset.tiImg = '1';
      if (img.complete && img.naturalWidth > 0) return;
      img.classList.add('ti-img-pending');
      const done = () => img.classList.remove('ti-img-pending');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
      window.setTimeout(done, 5000);
    };

    const scan = (node) => {
      if (node.matches && node.matches(selector)) track(node);
      if (node.querySelectorAll) node.querySelectorAll(selector).forEach(track);
    };

    scan(document);

    const main = document.querySelector('main');
    if (!main || !('MutationObserver' in window)) return;
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) scan(node);
        });
      });
    }).observe(main, { childList: true, subtree: true });
  }

  function init() {
    initImageFade();
    initScrollEngine();
    initReveals();
    initCollectionIndex();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
